import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileDown, RefreshCcw, Camera, X, RotateCcw, Check, Timer, Zap, ZapOff } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, createEntry, uploadEntryPicture, fileUrl } from "../lib/api";
import { generatePolaroidPicture, downloadBlob } from "../lib/picture";
import { PROPERTIES } from "../lib/properties";

export default function Sign() {
  const [searchParams] = useSearchParams();
  const autoCamera = searchParams.get("camera") === "1";
  const initialNest = searchParams.get("nest") || null;
  const [step, setStep] = useState(autoCamera ? 2 : 1);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [message, setMessage] = useState("");
  const [selectedNest, setSelectedNest] = useState(initialNest);

  const [uploading, setUploading] = useState(false);
  const [image, setImage] = useState(null); // { path, url, width, height }
  const [saving, setSaving] = useState(false);

  // Camera
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [previewUrl, setPreviewUrl] = useState(null); // captured photo preview
  const [timerSec, setTimerSec] = useState(0); // 0 | 3 | 10
  const [countdown, setCountdown] = useState(0);
  const [flashOn, setFlashOn] = useState(false);
  const [flashActive, setFlashActive] = useState(false); // white overlay
  const countdownTimerRef = useRef(null);
  const capturedFileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const imgWrapRef = useRef(null);
  const polaroidRef = useRef(null);
  const navigate = useNavigate();

  const canProceed = guestName.trim().length > 0;

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      // Get dims locally
      const dims = await new Promise((res, rej) => {
        const img = new Image();
        img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = rej;
        img.src = URL.createObjectURL(file);
      });
      const up = await uploadImage(file);
      setImage({
        path: up.path,
        url: fileUrl(up.path),
        width: dims.width,
        height: dims.height,
      });
      toast.success("Picture uploaded");
    } catch (e) {
      console.error(e);
      toast.error("Upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async (mode = facingMode) => {
    setCameraError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Your browser doesn't support camera access.");
      return;
    }
    try {
      stopStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.error(err);
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setCameraError("Camera permission was blocked. Enable it in your browser settings.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setCameraError("No camera was found on this device.");
      } else {
        setCameraError("Could not access the camera.");
      }
    }
  };

  const openCamera = () => {
    setCameraOpen(true);
  };

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
    setCameraError("");
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    capturedFileRef.current = null;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
    setFlashActive(false);
  };

  const flipCamera = () => {
    const next = facingMode === "user" ? "environment" : "user";
    setFacingMode(next);
    startCamera(next);
  };

  useEffect(() => {
    if (cameraOpen) {
      startCamera(facingMode);
    }
    return () => {
      if (!cameraOpen) stopStream();
    };
  }, [cameraOpen]);

  useEffect(() => () => stopStream(), []);

  // Auto-open camera when landing arrived via "Take a picture" CTA.
  useEffect(() => {
    if (autoCamera && step === 2 && !image && !cameraOpen) {
      openCamera();
    }
  }, [autoCamera, step, image]);

  const cycleTimer = () => {
    setTimerSec((s) => (s === 0 ? 3 : s === 3 ? 10 : 0));
  };

  const toggleFlash = () => setFlashOn((v) => !v);

  const enableTorch = async (on) => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      const caps = track?.getCapabilities?.();
      if (track && caps && "torch" in caps) {
        await track.applyConstraints({ advanced: [{ torch: !!on }] });
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  };

  const _doCapture = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error("Camera not ready yet");
      return;
    }
    // Trigger flash (hardware if supported, otherwise white-screen flash)
    let hardwareFlash = false;
    if (flashOn) {
      hardwareFlash = await enableTorch(true);
      if (!hardwareFlash) {
        setFlashActive(true);
        // let the white overlay actually paint before drawing the frame
        await new Promise((r) => setTimeout(r, 90));
      }
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (facingMode === "user") {
      // Mirror to match the on-screen preview
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    // Fade flash out
    if (flashOn) {
      if (hardwareFlash) {
        await enableTorch(false);
      } else {
        setTimeout(() => setFlashActive(false), 220);
      }
    }
    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", 0.92),
    );
    if (!blob) {
      toast.error("Couldn't capture the photo");
      return;
    }
    const file = new File([blob], `camera-${Date.now()}.jpg`, {
      type: "image/jpeg",
    });
    capturedFileRef.current = file;
    setPreviewUrl(URL.createObjectURL(blob));
    stopStream();
  };

  const capturePhoto = async () => {
    if (timerSec > 0) {
      // Start countdown
      setCountdown(timerSec);
      countdownTimerRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            // Fire capture right after clearing
            setTimeout(() => _doCapture(), 0);
            return 0;
          }
          return c - 1;
        });
      }, 1000);
    } else {
      await _doCapture();
    }
  };

  const retakePhoto = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    capturedFileRef.current = null;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdown(0);
    setFlashActive(false);
    startCamera(facingMode);
  };

  const confirmPhoto = async () => {
    const file = capturedFileRef.current;
    if (!file) return;
    const url = previewUrl;
    setPreviewUrl(null);
    capturedFileRef.current = null;
    setCameraOpen(false);
    stopStream();
    await handleFile(file);
    if (url) URL.revokeObjectURL(url);
  };

  const handleSaveAndDownload = async () => {
    if (!image) {
      toast.error("Please upload a picture first");
      return;
    }
    if (!guestName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSaving(true);
    try {
      // 1) save entry
      const entry = await createEntry({
        guest_name: guestName,
        guest_email: guestEmail || null,
        message: message || null,
        image_path: image.path,
        image_width: image.width,
        image_height: image.height,
        nest_id: selectedNest,
      });
      // 2) generate picture from DOM (capture the whole polaroid frame)
      const overlayNode = polaroidRef.current;
      const { blob } = await generatePolaroidPicture(overlayNode);
      // 3) upload picture (best-effort)
      try {
        await uploadEntryPicture(entry.id, blob);
      } catch (e) {
        console.warn("Picture upload failed", e);
      }
      // 4) trigger local download
      downloadBlob(
        blob,
        `hostbydims-${guestName.trim().replace(/\s+/g, "_")}.jpg`,
      );
      toast.success("Saved! Redirecting to your entry...");
      setTimeout(() => navigate(`/gallery/${entry.id}`), 800);
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-20 sm:pb-24">
      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-[#717171]">
        <span className={step === 1 ? "text-[#111111] font-semibold" : ""}>1. Your details</span>
        <span>→</span>
        <span className={step === 2 ? "text-[#111111] font-semibold" : ""}>2. Picture</span>
      </div>

      {step === 1 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 sm:mt-8 grid lg:grid-cols-2 gap-6 sm:gap-10 items-start"
        >
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111] tracking-tighter">
              Say hello, first.
            </h1>
            <p className="mt-4 text-[#5b5b5b] leading-relaxed">
              We&rsquo;ll add your name to the entry. Your email stays private
              and is only used if the host wants to send you a copy.
            </p>
          </div>
          <div className="gb-card p-6 sm:p-8 space-y-4 sm:space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#111] mb-2">
                Your name <span className="text-[#111111]">*</span>
              </label>
              <input
                data-testid="input-guest-name"
                className="gb-input"
                placeholder="e.g. Priya Menon"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111] mb-2">
                Email <span className="text-[#717171]">(optional)</span>
              </label>
              <input
                data-testid="input-guest-email"
                className="gb-input"
                type="email"
                placeholder="you@somewhere.com"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111] mb-2">
                A short message <span className="text-[#717171]">(optional)</span>
              </label>
              <textarea
                data-testid="input-guest-message"
                className="gb-input min-h-[110px] resize-none"
                placeholder="Thanks for having us! We loved the..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111] mb-2">
                Which nest are you staying at? <span className="text-[#717171]">(optional)</span>
              </label>
              <div className="grid grid-cols-2 gap-2.5" data-testid="nest-picker">
                {PROPERTIES.map((p) => {
                  const active = selectedNest === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`nest-option-${p.id}`}
                      onClick={() =>
                        setSelectedNest((cur) => (cur === p.id ? null : p.id))
                      }
                      className={`relative rounded-2xl overflow-hidden border transition-all text-left ${
                        active
                          ? "border-[#111] ring-2 ring-[#111]"
                          : "border-[#EBEBEB] hover:border-[#cfcfcf]"
                      }`}
                    >
                      <div className="aspect-[4/3] bg-[#f2f2f2]">
                        <img
                          src={p.image}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="px-2.5 py-2">
                        <p className="font-heading font-bold text-[13px] text-[#111] tracking-tight leading-none">
                          {p.name}
                        </p>
                        <p className="text-[11px] text-[#717171] mt-1 truncate">
                          {p.location}
                        </p>
                      </div>
                      {active && (
                        <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#111] text-white flex items-center justify-center shadow-md">
                          <Check size={13} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {selectedNest && (
                <button
                  type="button"
                  data-testid="nest-clear"
                  onClick={() => setSelectedNest(null)}
                  className="mt-2 text-[11px] text-[#717171] underline underline-offset-2 hover:text-[#111]"
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="pt-2">
              <button
                data-testid="btn-continue-to-annotate"
                onClick={() => setStep(2)}
                disabled={!canProceed}
                className="gb-btn-primary w-full"
              >
                Continue to the picture
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 grid lg:grid-cols-[1fr_320px] gap-8 items-start"
        >
          {/* CANVAS */}
          <div className="gb-card p-4 md:p-6">
            {!image && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                data-testid="drop-zone"
                className="dashed-drop flex flex-col items-center justify-center min-h-[420px] p-8 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#F4F4F4] flex items-center justify-center text-[#111111]">
                  <Upload size={22} />
                </div>
                <h3 className="mt-5 font-heading font-bold text-[#111] text-xl tracking-tight">
                  {uploading ? "Uploading..." : "Add a picture"}
                </h3>
                <p className="mt-2 text-[#717171] text-sm">
                  Drop a photo here, upload one, or use your camera. PNG, JPG or WEBP — up to 15MB.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <button
                    type="button"
                    data-testid="btn-choose-file"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="gb-btn-primary inline-flex items-center gap-2"
                  >
                    <Upload size={16} />
                    Choose file
                  </button>
                  <button
                    type="button"
                    data-testid="btn-open-camera"
                    onClick={openCamera}
                    disabled={uploading}
                    className="gb-btn-ghost inline-flex items-center gap-2"
                  >
                    <Camera size={16} />
                    Use camera
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  id="image-input"
                  data-testid="input-image"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
              </div>
            )}

            {image && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
                      Step 2
                    </p>
                    <h3 className="font-heading font-bold text-[#111] text-xl tracking-tight">
                      Add a note under your picture
                    </h3>
                  </div>
                  <button
                    data-testid="btn-change-picture"
                    onClick={() => {
                      setImage(null);
                    }}
                    className="gb-btn-ghost inline-flex items-center gap-2 text-sm"
                  >
                    <RefreshCcw size={14} /> Change picture
                  </button>
                </div>

                <div className="flex justify-center">
                  <div
                    ref={polaroidRef}
                    data-testid="polaroid-frame"
                    className="polaroid tilt-left w-full max-w-md"
                  >
                    <div className="polaroid-tape" aria-hidden="true" />
                    <div
                      ref={imgWrapRef}
                      data-testid="annotation-canvas"
                      className="polaroid-photo select-none"
                    >
                      <img
                        src={image.url}
                        alt="Guest upload"
                        className="w-full h-full object-cover pointer-events-none"
                        crossOrigin="anonymous"
                      />
                    </div>
                    <div className="polaroid-caption">
                      {guestName || "A guest"}
                      <span className="sub">hostbydims · Guest Gallery</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="gb-card p-5 sm:p-6 lg:sticky lg:top-24">
            <h4 className="font-heading font-bold text-[#111] text-lg tracking-tight">
              Your entry
            </h4>
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#717171] mb-1">
                  Your name <span className="text-[#111111]">*</span>
                </label>
                <input
                  data-testid="input-guest-name"
                  className="gb-input py-2 text-sm"
                  placeholder="e.g. Priya Menon"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#717171] mb-1">
                  Email <span className="text-[#a1a1a1]">(optional)</span>
                </label>
                <input
                  data-testid="input-guest-email"
                  className="gb-input py-2 text-sm"
                  type="email"
                  placeholder="you@somewhere.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#717171] mb-1">
                  Message <span className="text-[#a1a1a1]">(optional)</span>
                </label>
                <textarea
                  data-testid="input-guest-message"
                  className="gb-input py-2 text-sm resize-none min-h-[64px]"
                  placeholder="Thanks for having us!"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#717171] mb-1">
                  Staying at <span className="text-[#a1a1a1]">(optional)</span>
                </label>
                <select
                  data-testid="nest-select"
                  className="gb-input py-2 text-sm"
                  value={selectedNest || ""}
                  onChange={(e) => setSelectedNest(e.target.value || null)}
                >
                  <option value="">Prefer not to say</option>
                  {PROPERTIES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.location}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              data-testid="btn-save-and-download"
              onClick={handleSaveAndDownload}
              disabled={saving || !image || !guestName.trim()}
              className="gb-btn-primary w-full mt-6 inline-flex items-center justify-center gap-2"
            >
              {saving ? (
                "Saving..."
              ) : (
                <>
                  <FileDown size={16} /> Save & Download Picture
                </>
              )}
            </button>
            {!autoCamera && (
              <button
                data-testid="btn-back-to-info"
                onClick={() => setStep(1)}
                className="mt-3 w-full text-sm text-[#717171] hover:text-[#111]"
              >
                ← Back to your details
              </button>
            )}
          </aside>
        </motion.div>
      )}

      {cameraOpen && (
        <div
          data-testid="camera-modal"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
          onClick={closeCamera}
        >
          <div
            className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden w-full max-w-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-[#EBEBEB]">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#F4F4F4] flex items-center justify-center text-[#111111]">
                  <Camera size={16} />
                </span>
                <h3 className="font-heading font-bold text-[#111] tracking-tight text-sm sm:text-base">
                  Take a photo
                </h3>
              </div>
              <button
                data-testid="btn-close-camera"
                onClick={closeCamera}
                className="w-9 h-9 rounded-full hover:bg-[#f2eeea] flex items-center justify-center text-[#111]"
                aria-label="Close camera"
              >
                <X size={18} />
              </button>
            </div>
            <div className="relative bg-black aspect-[3/4] sm:aspect-video">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Captured preview"
                  data-testid="camera-preview"
                  className="w-full h-full object-contain bg-black"
                />
              ) : (
                <video
                  ref={videoRef}
                  data-testid="camera-video"
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{
                    transform: facingMode === "user" ? "scaleX(-1)" : "none",
                  }}
                />
              )}
              {/* Camera options overlay (timer + flash) */}
              {!previewUrl && !cameraError && (
                <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                  <button
                    type="button"
                    data-testid="btn-timer-toggle"
                    onClick={cycleTimer}
                    aria-label="Timer"
                    className={`h-9 min-w-9 px-2.5 rounded-full backdrop-blur-md text-xs font-semibold flex items-center gap-1 transition-colors ${
                      timerSec > 0
                        ? "bg-white text-[#111]"
                        : "bg-black/45 text-white hover:bg-black/60"
                    }`}
                  >
                    <Timer size={14} />
                    {timerSec > 0 ? `${timerSec}s` : "Off"}
                  </button>
                  <button
                    type="button"
                    data-testid="btn-flash-toggle"
                    onClick={toggleFlash}
                    aria-label="Flash"
                    className={`h-9 w-9 rounded-full backdrop-blur-md flex items-center justify-center transition-colors ${
                      flashOn
                        ? "bg-white text-[#111]"
                        : "bg-black/45 text-white hover:bg-black/60"
                    }`}
                  >
                    {flashOn ? <Zap size={16} /> : <ZapOff size={16} />}
                  </button>
                </div>
              )}
              {/* Countdown big number */}
              {countdown > 0 && (
                <div
                  data-testid="camera-countdown"
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <span
                    key={countdown}
                    className="font-heading font-extrabold text-white text-[120px] leading-none drop-shadow-[0_6px_18px_rgba(0,0,0,0.35)] animate-[pulse_1s_ease-out_1]"
                  >
                    {countdown}
                  </span>
                </div>
              )}
              {/* White software-flash overlay */}
              {flashActive && (
                <div
                  data-testid="camera-flash-overlay"
                  className="absolute inset-0 bg-white pointer-events-none"
                  style={{
                    opacity: 0.9,
                    transition: "opacity 220ms ease-out",
                  }}
                />
              )}
              {cameraError && !previewUrl && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-center p-6 sm:p-8">
                  <div>
                    <p className="font-heading font-bold text-base sm:text-lg">
                      Camera unavailable
                    </p>
                    <p className="text-sm text-white/80 mt-2">{cameraError}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
              {previewUrl ? (
                <>
                  <button
                    data-testid="btn-retake-photo"
                    onClick={retakePhoto}
                    className="gb-btn-ghost inline-flex items-center gap-2"
                  >
                    <RotateCcw size={16} />
                    Retake
                  </button>
                  <button
                    data-testid="btn-confirm-photo"
                    onClick={confirmPhoto}
                    disabled={uploading}
                    className="gb-btn-primary inline-flex items-center gap-2"
                  >
                    <Check size={16} />
                    Use this photo
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="btn-flip-camera"
                    onClick={flipCamera}
                    disabled={!!cameraError}
                    className="gb-btn-ghost inline-flex items-center gap-2"
                  >
                    <RotateCcw size={16} />
                    Flip
                  </button>
                  <button
                    data-testid="btn-capture-photo"
                    onClick={capturePhoto}
                    disabled={!!cameraError || uploading || countdown > 0}
                    className="gb-btn-primary inline-flex items-center gap-2"
                  >
                    <Camera size={16} />
                    {countdown > 0 ? `In ${countdown}s...` : "Capture"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
